import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { initTracking, disableTracking, initTrackingIfConsented } from "./utils/tracking";
import { useLanguage } from "./contexts/LanguageContext";
import "./css/cookieconsent.css";

const CookieConsent = () => {
  const [showConsent, setShowConsent] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [adsEnabled, setAdsEnabled] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const userConsent = Cookies.get("userConsent");
    if (!userConsent) {
      setShowConsent(true);
    } else if (userConsent === "accepted") {
      // If consent was previously accepted, initialize tracking
      initTracking();
    } else if (userConsent === "custom") {
      // Check for custom preferences
      const analyticsPref = Cookies.get("analyticsEnabled");
      const adsPref = Cookies.get("adsEnabled");
      
      if (analyticsPref === "true" || adsPref === "true") {
        // If either analytics or ads are enabled, initialize tracking
        initTracking();
      } else {
        // Both disabled
        disableTracking();
      }
    }
  }, []);

  const handleAccept = () => {
    Cookies.set("userConsent", "accepted", { 
      expires: 365,
      sameSite: 'Strict',
      secure: true
    });
    // Initialize tracking
    initTracking();
    setShowConsent(false);
  };

  const handleDeny = () => {
    Cookies.set("userConsent", "denied", { 
      expires: 365,
      sameSite: 'Strict',
      secure: true
    });
    // Disable all tracking
    disableTracking();
    setShowConsent(false);
  };

  const handleCustomize = () => {
    setShowPreferences(true);
  };

  const handleSavePreferences = () => {
    Cookies.set("userConsent", "custom", { 
      expires: 365,
      sameSite: 'Strict',
      secure: true
    });
    Cookies.set("analyticsEnabled", analyticsEnabled.toString(), { 
      expires: 365,
      sameSite: 'Strict',
      secure: true
    });
    Cookies.set("adsEnabled", adsEnabled.toString(), { 
      expires: 365,
      sameSite: 'Strict',
      secure: true
    });
    
    if (analyticsEnabled || adsEnabled) {
      // If either analytics or ads are enabled, initialize tracking
      initTracking();
    } else {
      // Both disabled
      disableTracking();
    }
    
    setShowPreferences(false);
    setShowConsent(false);
  };

  const handleClosePreferences = () => {
    setShowPreferences(false);
  };

  const toggleAnalytics = () => {
    setAnalyticsEnabled(!analyticsEnabled);
  };

  const toggleAds = () => {
    setAdsEnabled(!adsEnabled);
  };

  if (!showConsent) {
    return null;
  }

  return (
    <>
      <div className="cookie-consent-container">
        <div className="cookie-consent-content">
          <p className="cookie-consent-text">
            {t("cookie.message")}
          </p>
          <div className="cookie-consent-buttons">
            <button onClick={handleDeny} className="cookie-consent-button cookie-consent-button-deny">
              {t("cookie.deny")}
            </button>
            <button onClick={handleCustomize} className="cookie-consent-button cookie-consent-button-customize">
              {t("cookie.customize")}
            </button>
            <button onClick={handleAccept} className="cookie-consent-button cookie-consent-button-accept">
              {t("cookie.accept")}
            </button>
          </div>
        </div>
      </div>

      {showPreferences && (
        <div className="cookie-modal-overlay">
          <div className="cookie-modal-content">
            <div className="cookie-modal-header">
              <h3 className="cookie-modal-title">{t("cookie.preferences.title")}</h3>
              <button onClick={handleClosePreferences} className="cookie-modal-close">
                &times;
              </button>
            </div>
            <div className="cookie-modal-body">
              <p className="cookie-preference-text">
                {t("cookie.preferences.description")}
              </p>
              
              <div className="cookie-preference-option">
                <div className="cookie-preference-header">
                  <span className="cookie-preference-title">{t("cookie.preferences.analytics.title")}</span>
                  <label className="cookie-toggle-switch">
                    <input 
                      type="checkbox" 
                      className="cookie-toggle-input"
                      checked={analyticsEnabled}
                      onChange={toggleAnalytics}
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
                <p className="cookie-preference-description">
                  {t("cookie.preferences.analytics.description")}
                </p>
              </div>

              <div className="cookie-preference-option">
                <div className="cookie-preference-header">
                  <span className="cookie-preference-title">{t("cookie.preferences.ads.title")}</span>
                  <label className="cookie-toggle-switch">
                    <input 
                      type="checkbox" 
                      className="cookie-toggle-input"
                      checked={adsEnabled}
                      onChange={toggleAds}
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
                <p className="cookie-preference-description">
                  {t("cookie.preferences.ads.description")}
                </p>
              </div>

              <div className="cookie-preference-option">
                <div className="cookie-preference-header">
                  <span className="cookie-preference-title">{t("cookie.preferences.essential.title")}</span>
                  <span className="cookie-always-on">{t("cookie.preferences.alwaysOn")}</span>
                </div>
                <p className="cookie-preference-description">
                  {t("cookie.preferences.essential.description")}
                </p>
              </div>
            </div>
            <div className="cookie-modal-footer">
              <button onClick={handleClosePreferences} className="cookie-modal-button cookie-modal-button-cancel">
                {t("cookie.preferences.cancel")}
              </button>
              <button onClick={handleSavePreferences} className="cookie-modal-button cookie-modal-button-save">
                {t("cookie.preferences.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
